import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Plus, X, Pencil, Trash2, Check, Flag, MessageSquare, Layers } from 'lucide-react';
import CommentsModal from '../components/CommentsModal';

const COLUMNS = [
  { key: 'todo', label: 'To Do', color: '#a0a0a0' },
  { key: 'in_progress', label: 'In Progress', color: '#eab308' },
  { key: 'review', label: 'Review', color: '#a855f7' },
  { key: 'done', label: 'Done', color: '#22c55e' },
];
const priorityColor = { low: 'badge-gray', medium: 'badge-blue', high: 'badge-yellow', urgent: 'badge-red' };
const statusColor = { planning: 'badge-blue', in_progress: 'badge-yellow', review: 'badge-purple', completed: 'badge-green', on_hold: 'badge-gray' };

const TaskModal = ({ onClose, onSave, projectId, users, initial }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(
    initial
      ? { ...initial, assignedTo: initial.assignedTo || '', dueDate: initial.dueDate ? initial.dueDate.split('T')[0] : '' }
      : { title: '', description: '', priority: 'medium', status: 'todo', dueDate: '', assignedTo: '', projectId }
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, assignedTo: form.assignedTo === '' ? null : form.assignedTo, dueDate: form.dueDate === '' ? null : form.dueDate };
    try {
      if (isEdit) await axios.put(`/api/tasks/${initial.id}`, payload);
      else await axios.post('/api/tasks', payload);
      onSave();
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Task' : 'Add Task'}</div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="input-group"><label className="input-label">Title *</label><input className="input" required value={form.title} onChange={e => set('title', e.target.value)} placeholder="Task title..." /></div>
            <div className="grid-2">
              <div className="input-group"><label className="input-label">Priority</label>
                <select className="input select" value={form.priority} onChange={e => set('priority', e.target.value)}>
                  {['low', 'medium', 'high', 'urgent'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="input-group"><label className="input-label">Status</label>
                <select className="input select" value={form.status} onChange={e => set('status', e.target.value)}>
                  {['todo', 'in_progress', 'review', 'done'].map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </div>
            </div>
            <div className="grid-2">
              <div className="input-group"><label className="input-label">Due Date</label><input type="date" className="input" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} /></div>
              <div className="input-group"><label className="input-label">Assign To</label>
                <select className="input select" value={form.assignedTo} onChange={e => set('assignedTo', e.target.value)}>
                  <option value="">Unassigned</option>{users.filter(u => u.role !== 'client').map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>
            <div className="input-group"><label className="input-label">Description</label><textarea className="input" rows={2} value={form.description || ''} onChange={e => set('description', e.target.value)} /></div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary">{isEdit ? 'Save' : 'Add Task'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = ['admin', 'manager'].includes(user?.role);
  const isClient = user?.role === 'client';

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskModal, setTaskModal] = useState(null);
  const [commentsModal, setCommentsModal] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [newMilestone, setNewMilestone] = useState({ title: '', dueDate: '' });
  const [savingMilestones, setSavingMilestones] = useState(false);

  // Services state
  const [services, setServices] = useState([]);
  const [newServiceName, setNewServiceName] = useState('');
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editingServiceName, setEditingServiceName] = useState('');

  const loadProject = () => axios.get(`/api/projects/${id}`).then(r => { setProject(r.data.data); setMilestones(r.data.data.milestones || []); });
  const loadTasks = () => axios.get(`/api/tasks/project/${id}`).then(r => setTasks(r.data.data));
  const loadUsers = () => canManage ? axios.get('/api/users').then(r => setUsers(r.data.data)) : Promise.resolve();
  const loadServices = useCallback(() => axios.get(`/api/project-types/project/${id}/services`).then(r => setServices(r.data.data)).catch(() => {}), [id]);

  const load = () => {
    setLoading(true);
    Promise.all([loadProject(), loadTasks(), loadUsers(), loadServices()]).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const addService = async () => {
    if (!newServiceName.trim()) return;
    try {
      const r = await axios.post(`/api/project-types/project/${id}/services`, { name: newServiceName.trim() });
      setServices(prev => [...prev, r.data.data]);
      setNewServiceName('');
    } catch { alert('فشل الإضافة'); }
  };

  const toggleService = async (service) => {
    try {
      const r = await axios.put(`/api/project-types/project/${id}/services/${service.id}`, { isDone: !service.isDone });
      setServices(prev => prev.map(s => s.id === service.id ? r.data.data : s));
    } catch { alert('Error'); }
  };

  const saveServiceName = async (service) => {
    if (!editingServiceName.trim()) return;
    try {
      const r = await axios.put(`/api/project-types/project/${id}/services/${service.id}`, { name: editingServiceName.trim() });
      setServices(prev => prev.map(s => s.id === service.id ? r.data.data : s));
    } catch { alert('Error'); }
    setEditingServiceId(null);
  };

  const deleteService = async (serviceId) => {
    try {
      await axios.delete(`/api/project-types/project/${id}/services/${serviceId}`);
      setServices(prev => prev.filter(s => s.id !== serviceId));
    } catch { alert('فشل الحذف'); }
  };

  const handleDragStart = (e, taskId) => { e.dataTransfer.setData('taskId', taskId); e.currentTarget.classList.add('dragging'); };
  const handleDragEnd = (e) => { e.currentTarget.classList.remove('dragging'); };
  const handleDragOver = (e, colKey) => { e.preventDefault(); setDragOver(colKey); };
  const handleDrop = async (e, colKey) => {
    e.preventDefault(); setDragOver(null);
    if (isClient) return;
    const taskId = e.dataTransfer.getData('taskId');
    const task = tasks.find(t => t.id === taskId);
    if (task && task.status !== colKey) {
      try { await axios.put(`/api/tasks/${taskId}`, { status: colKey }); loadTasks(); loadProject(); }
      catch { alert('Failed to update task'); }
    }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try { await axios.delete(`/api/tasks/${task.id}`); loadTasks(); loadProject(); }
    catch { alert('Failed to delete task'); }
  };

  const saveMilestones = async (updated) => {
    setSavingMilestones(true);
    try { await axios.put(`/api/projects/${id}`, { milestones: updated }); setMilestones(updated); }
    catch { alert('Failed to save milestones'); }
    finally { setSavingMilestones(false); }
  };

  const toggleMilestone = (i) => { const u = milestones.map((m, idx) => idx === i ? { ...m, completed: !m.completed } : m); saveMilestones(u); };
  const deleteMilestone = (i) => { const u = milestones.filter((_, idx) => idx !== i); saveMilestones(u); };
  const addMilestone = () => {
    if (!newMilestone.title.trim()) return;
    saveMilestones([...milestones, { title: newMilestone.title.trim(), dueDate: newMilestone.dueDate || null, completed: false }]);
    setNewMilestone({ title: '', dueDate: '' });
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;
  if (!project) return <div style={{ padding: 32, color: 'var(--red)' }}>Project not found.</div>;

  const doneTasks = tasks.filter(t => t.status === 'done').length;

  return (
    <div className="page-content">
      {taskModal && (
        <TaskModal onClose={() => setTaskModal(null)} onSave={() => { setTaskModal(null); loadTasks(); loadProject(); }}
          projectId={id} users={users} initial={taskModal.task || undefined} />
      )}
      {commentsModal && (
        <CommentsModal task={commentsModal.task} onClose={() => setCommentsModal(null)} currentUser={user} />
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => navigate('/projects')} className="btn btn-secondary btn-sm" style={{ marginBottom: 16 }}>
          <ArrowLeft size={14} /> Back to Projects
        </button>
        <div className="flex-between">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <h1 style={{ fontSize: 22, fontWeight: 700 }}>{project.name}</h1>
              <span className={`badge ${statusColor[project.status] || 'badge-gray'}`}>{project.status?.replace('_', ' ')}</span>
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
              {project.client?.companyName}{project.manager ? ` � Manager: ${project.manager.name}` : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="detail-stats">
        <div className="detail-stat">
          <div className="detail-stat-label">Progress</div>
          <div className="detail-stat-value">{project.progress}%</div>
          <div className="progress-bar" style={{ marginTop: 8, height: 4 }}><div className="progress-fill" style={{ width: `${project.progress}%` }} /></div>
        </div>
        <div className="detail-stat"><div className="detail-stat-label">Tasks</div><div className="detail-stat-value">{doneTasks} / {tasks.length} done</div></div>
        <div className="detail-stat"><div className="detail-stat-label">Budget</div><div className="detail-stat-value">{project.budget ? `$${Number(project.budget).toLocaleString()}` : '�'}</div></div>
        <div className="detail-stat"><div className="detail-stat-label">Deadline</div><div className="detail-stat-value" style={{ fontSize: 14 }}>{project.endDate ? new Date(project.endDate).toLocaleDateString() : '�'}</div></div>
      </div>

      {project.description && (
        <div className="card" style={{ marginBottom: 24, padding: '14px 20px' }}>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{project.description}</p>
        </div>
      )}

      {/* Main Layout */}
      <div className="detail-layout">
        {/* Kanban */}
        <div>
          <div className="flex-between" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Tasks</div>
            {canManage && <button className="btn btn-primary btn-sm" onClick={() => setTaskModal({})}><Plus size={14} /> Add Task</button>}
          </div>
          {!tasks.length ? (
            <div className="empty-state" style={{ padding: '40px 20px' }}><p>No tasks yet</p></div>
          ) : (
            <div className="kanban">
              {COLUMNS.map(col => {
                const colTasks = tasks.filter(t => t.status === col.key);
                return (
                  <div key={col.key}
                    className={`kanban-col${dragOver === col.key && !isClient ? ' drag-over' : ''}`}
                    onDragOver={e => !isClient && handleDragOver(e, col.key)}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={e => handleDrop(e, col.key)}>
                    <div className="kanban-col-title">
                      <span style={{ color: col.color }}>{col.label}</span>
                      <span style={{ background: 'var(--bg-hover)', borderRadius: 12, padding: '1px 8px', fontSize: 12 }}>{colTasks.length}</span>
                    </div>
                    {colTasks.map(task => (
                      <div key={task.id} className="kanban-card"
                        draggable={!isClient}
                        onDragStart={!isClient ? e => handleDragStart(e, task.id) : undefined}
                        onDragEnd={!isClient ? handleDragEnd : undefined}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                          <div className="kanban-card-title" style={{ flex: 1, marginBottom: 0 }}>{task.title}</div>
                          {canManage && (
                            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                              <button onClick={() => setTaskModal({ task: { ...task, assignedTo: task.assignedTo || '' } })} title="Edit"
                                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 4px' }}>
                                <Pencil size={12} />
                              </button>
                              <button onClick={() => deleteTask(task)} title="Delete"
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px 4px' }}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="kanban-card-meta">
                          <span className={`badge ${priorityColor[task.priority]}`} style={{ fontSize: 11 }}>{task.priority}</span>
                          {task.assignee && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{task.assignee.name}</span>}
                        </div>
                        {task.dueDate && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Due: {new Date(task.dueDate).toLocaleDateString()}</div>}
                        {/* Comments button */}
                        <button onClick={() => setCommentsModal({ task })}
                          style={{ marginTop: 8, background: 'none', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer', padding: '4px 8px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, width: '100%', justifyContent: 'center', transition: 'all 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-light)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}>
                          <MessageSquare size={11} />
                          {task.comments?.length > 0 ? `${task.comments.length} comment${task.comments.length > 1 ? 's' : ''}` : 'Comment'}
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div>
          {/* Milestones */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Flag size={16} style={{ color: 'var(--text-muted)' }} />
              <div style={{ fontSize: 15, fontWeight: 600 }}>Milestones</div>
              <span style={{ background: 'var(--bg-hover)', borderRadius: 12, padding: '1px 8px', fontSize: 12, color: 'var(--text-muted)' }}>{milestones.filter(m => m.completed).length}/{milestones.length}</span>
            </div>
            {!milestones.length && <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0' }}>No milestones yet</div>}
            {milestones.map((m, i) => (
              <div key={i} className="milestone-item">
                <div className={`milestone-check${m.completed ? ' done' : ''}`} onClick={() => toggleMilestone(i)}>
                  {m.completed && <Check size={10} color="#fff" strokeWidth={3} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div className={`milestone-title${m.completed ? ' done' : ''}`}>{m.title}</div>
                  {m.dueDate && <div className="milestone-date">{new Date(m.dueDate).toLocaleDateString()}</div>}
                </div>
                {canManage && <button onClick={() => deleteMilestone(i)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px 4px', opacity: 0.6 }}><X size={13} /></button>}
              </div>
            ))}
            {canManage && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <input className="input" style={{ marginBottom: 8 }} placeholder="Milestone title..." value={newMilestone.title}
                  onChange={e => setNewMilestone(m => ({ ...m, title: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addMilestone())} />
                <input type="date" className="input" style={{ marginBottom: 8 }} value={newMilestone.dueDate}
                  onChange={e => setNewMilestone(m => ({ ...m, dueDate: e.target.value }))} />
                <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={addMilestone} disabled={savingMilestones}>
                  <Plus size={14} /> Add Milestone
                </button>
              </div>
            )}
          </div>

          {/* Services */}
          <div className="card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Layers size={16} style={{ color: 'var(--text-muted)' }} />
              <div style={{ fontSize: 15, fontWeight: 600 }}>الخدمات</div>
              <span style={{ background: 'var(--bg-hover)', borderRadius: 12, padding: '1px 8px', fontSize: 12, color: 'var(--text-muted)' }}>
                {services.filter(s => s.isDone).length}/{services.length}
              </span>
            </div>

            {!services.length && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '10px 0' }}>
                لا توجد خدمات بعد
              </div>
            )}

            {services.map(service => (
              <div key={service.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                {/* Checkbox */}
                <div onClick={() => toggleService(service)}
                  style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${service.isDone ? 'var(--accent)' : 'var(--border)'}`, background: service.isDone ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                  {service.isDone && <Check size={11} color="#fff" strokeWidth={3} />}
                </div>

                {editingServiceId === service.id ? (
                  <>
                    <input className="input" style={{ flex: 1, padding: '3px 8px', fontSize: 12 }}
                      value={editingServiceName}
                      onChange={e => setEditingServiceName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveServiceName(service); if (e.key === 'Escape') setEditingServiceId(null); }}
                      autoFocus
                    />
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 2 }} onClick={() => saveServiceName(service)}><Check size={13} /></button>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }} onClick={() => setEditingServiceId(null)}><X size={13} /></button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontSize: 13, textDecoration: service.isDone ? 'line-through' : 'none', color: service.isDone ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                      {service.name}
                    </span>
                    {canManage && (
                      <>
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}
                          onClick={() => { setEditingServiceId(service.id); setEditingServiceName(service.name); }}>
                          <Pencil size={12} />
                        </button>
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}
                          onClick={() => deleteService(service.id)}>
                          <X size={12} />
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            ))}

            {canManage && (
              <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                <input className="input" style={{ flex: 1, padding: '5px 8px', fontSize: 12 }}
                  placeholder="خدمة جديدة..."
                  value={newServiceName}
                  onChange={e => setNewServiceName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addService()}
                />
                <button className="btn btn-secondary btn-sm" onClick={addService} disabled={!newServiceName.trim()}>
                  <Plus size={13} />
                </button>
              </div>
            )}
          </div>

          {/* Project Details */}
          <div className="card" style={{ marginTop: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Details</div>
            {[
              { label: 'Client', value: project.client?.companyName },
              { label: 'Type', value: project.projectType?.name || project.type?.replace('_', ' ') },
              { label: 'Manager', value: project.manager?.name || 'Unassigned' },
              { label: 'Start Date', value: project.startDate ? new Date(project.startDate).toLocaleDateString() : '—' },
              { label: 'End Date', value: project.endDate ? new Date(project.endDate).toLocaleDateString() : '—' },
              { label: 'Budget', value: project.budget ? `$${Number(project.budget).toLocaleString()}` : '—' },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{value || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
